import { useState } from "react";
import { saveAs } from "file-saver";
import {
  CompareBucket,
  CompareBy,
  OrganizationSubjectAdminResponse,
} from "client/openapi";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import {
  MEASURES,
  Measure,
  Row,
  UNIT,
  alignRows,
  bucketDates,
  format,
  group,
  percentChange,
  shows,
  sumRows,
  tagOf,
  toCsv,
  valueOf,
} from "./data";
import {
  GroupPills,
  StatusBlock,
  SubjectFilter,
  SubjectPills,
} from "./controls";
import {
  ComparisonQuery,
  remember,
  remembered,
  useComparison,
} from "./useComparison";

type Props = Omit<ComparisonQuery, "subjectIds"> & {
  subjects: OrganizationSubjectAdminResponse[];
};

export default function PeriodTable({ subjects, ...query }: Props) {
  const { orgId, range } = query;
  const [by, setBy] = useState<CompareBy>(() =>
    remembered(orgId, "tableBy", CompareBy.MONTH)
  );
  const [subjectIds, setSubjectIds] = useState<number[]>([]);
  const { data, failed, loading } = useComparison({ ...query, subjectIds });
  const regroup = (value: CompareBy) => {
    setBy(value);
    remember(orgId, "tableBy", value);
  };
  const rows = data
    ? alignRows(
        group(data.current.buckets, by),
        data.compared ? group(data.compared.buckets, by) : null
      )
    : [];
  const withAgainst = !!data?.compared;

  const subjectNames = subjects
    .filter((s) => subjectIds.includes(s.id))
    .map((s) => s.name)
    .join(" ");
  const exportCsv = () =>
    data &&
    saveAs(
      new Blob([toCsv(rows, by, withAgainst, query.basis)], {
        type: "text/csv;charset=utf-8",
      }),
      [
        "financials",
        subjectNames.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, ""),
        `${query.range[0]}-to-${query.range[1]}`,
      ]
        .filter(Boolean)
        .join("-") + ".csv"
    );

  return (
    <div className="space-y-4 bg-blue-50/50 p-6 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex flex-wrap items-center gap-4">
        <GroupPills by={by} onChange={regroup} />
        <SubjectFilter
          subjects={subjects}
          value={subjectIds}
          onChange={setSubjectIds}
        />
        <div className="ml-auto">
          <Button
            color={ButtonColor.DARK_BLUE}
            size={ButtonSize.SMALL}
            fill={ButtonFill.HOLLOW}
            onClick={exportCsv}
            // Not while reloading: it would save the old rows.
            disabled={!data || loading}
          >
            Export CSV
          </Button>
        </div>
      </div>
      <SubjectPills
        subjects={subjects}
        value={subjectIds}
        onChange={setSubjectIds}
      />
      {failed ? (
        <StatusBlock>{failed}</StatusBlock>
      ) : !data ? (
        <StatusBlock>
          <div
            className="spinner-border text-gray-400"
            role="status"
            aria-label="Loading"
          />
          Loading...
        </StatusBlock>
      ) : (
        // On a phone the table scrolls sideways in its own box, not the page.
        <div className={`overflow-x-auto ${loading ? "opacity-60" : ""}`}>
          <Table
            rows={rows}
            by={by}
            withAgainst={withAgainst}
            basis={query.basis}
          />
        </div>
      )}
    </div>
  );
}

function Change({
  now,
  then,
  measure,
}: {
  now: number;
  then: number;
  measure: Measure;
}) {
  const pct = percentChange(now, then);
  if (pct === null) return null;
  return (
    <span className="text-gray-600">
      {pct >= 0 ? "▲" : "▼"} {Math.abs(pct).toFixed(1)}%
      <span className="sr-only"> change in {measure}</span>
    </span>
  );
}

function Table({
  rows,
  by,
  withAgainst,
  basis,
}: {
  rows: Row[];
  by: CompareBy;
  withAgainst: boolean;
  basis: ComparisonQuery["basis"];
}) {
  const unit = UNIT[by];
  const cell = (
    measure: Measure,
    now: CompareBucket | undefined,
    then: CompareBucket | undefined,
    tag: string | null,
    key: string
  ) => {
    const a = shows(now) ? valueOf(now, measure) : null;
    const b = shows(then) ? valueOf(then, measure) : null;
    return (
      <td
        key={key}
        className="py-2.5 pl-4 text-right align-top whitespace-nowrap"
      >
        <div className="font-medium text-slate-800">
          {a === null ? "—" : format(measure, a)}
          {tag && a !== null && (
            <span className="font-normal text-gray-500"> {tag}</span>
          )}
        </div>
        {withAgainst && b !== null && (
          <div className="text-xs text-gray-500 space-x-1.5">
            <span>{format(measure, b)}</span>
            {/* The value shown less the compared value shown. */}
            {a !== null && <Change now={a} then={b} measure={measure} />}
          </div>
        )}
      </td>
    );
  };
  return (
    <table className="w-full text-sm tabular-nums">
      <thead>
        <tr className="text-left text-gray-500 border-b border-gray-200">
          <th className="py-2 font-semibold">{unit}</th>
          {MEASURES.map((m) => (
            <th key={m.value} className="py-2 pl-4 font-semibold text-right">
              {m.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const { current, compared } = row;
          const tag = tagOf(current, basis);
          return (
            <tr key={row.index} className="border-b border-gray-100">
              <td className="py-2.5 align-top whitespace-nowrap">
                {by === CompareBy.WEEK || !current ? (
                  <div className="font-medium text-slate-800">
                    {unit} {row.index}
                  </div>
                ) : null}
                {current && (
                  <div
                    className={
                      by === CompareBy.WEEK
                        ? "text-xs text-gray-600"
                        : "font-medium text-slate-800"
                    }
                  >
                    {bucketDates(current, by)}
                  </div>
                )}
                {withAgainst && compared && (
                  <div className="text-xs text-gray-400">
                    {bucketDates(compared, by)}
                  </div>
                )}
              </td>
              {MEASURES.map((m) =>
                cell(m.value, current, compared, tag, m.value)
              )}
            </tr>
          );
        })}
        <tr className="font-semibold">
          <td className="pt-3 align-top text-slate-800">Total</td>
          {MEASURES.map((m) =>
            cell(
              m.value,
              sumRows(rows, "current"),
              withAgainst ? sumRows(rows, "compared") : undefined,
              null,
              m.value
            )
          )}
        </tr>
      </tbody>
    </table>
  );
}
